import React from 'react';
import { Check, Sparkles, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Enhanced Template Card Component
 * Beautiful card with hover effects, detailed information, and autumn color scheme
 */
const TemplateCard = ({ template, isSelected, onSelect, isPopular = false }) => {
    const navigate = useNavigate();

    const handleViewTemplate = (e) => {
        e.stopPropagation();
        navigate(`/template${template.id}`);
    };

    const handleSelectTemplate = (e) => {
        e.stopPropagation();
        onSelect(template);
    };

    return (
        <div className={`group relative bg-white rounded-3xl overflow-hidden transition-all duration-500 transform hover:-translate-y-2 ${
                isSelected
                    ? 'ring-4 ring-amber-500 shadow-2xl scale-[1.02]'
                    : 'shadow-xl hover:shadow-2xl border border-amber-100 hover:border-amber-300'
            }`}>

            {/* Popular Badge */}
            {isPopular && (
                <div className="absolute top-6 right-6 z-10 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Popular Choice
                </div>
            )}

            {/* Selected Checkmark */}
            {isSelected && (
                <div className="absolute top-6 left-6 z-10 bg-amber-600 text-white p-3 rounded-full shadow-lg">
                    <Check className="w-6 h-6" />
                </div>
            )}

            {/* Template Image */}
            <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-amber-50 to-orange-100">
                <img
                    src={template.image}
                    alt={template.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/800x600/F59E0B/FFFFFF?text=' + encodeURIComponent(template.name);
                    }}
                />

                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Hover Action Buttons */}
                <div className="absolute inset-0 flex items-center justify-center gap-4 opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <button
                        onClick={handleViewTemplate}
                        className="bg-white text-amber-700 px-6 py-3 rounded-full font-semibold shadow-xl transform translate-y-6 group-hover:translate-y-0 transition-all duration-500 delay-75 hover:bg-amber-50 flex items-center gap-2"
                    >
                        <Eye className="w-4 h-4" />
                        View Template
                    </button>
                    <button
                        onClick={handleSelectTemplate}
                        className="bg-gradient-to-r from-amber-600 to-orange-600 text-white px-6 py-3 rounded-full font-semibold shadow-xl transform translate-y-6 group-hover:translate-y-0 transition-all duration-500 delay-150 hover:from-amber-700 hover:to-orange-700"
                    >
                        Select Template
                    </button>
                </div>
            </div>

            {/* Template Info */}
            <div className="p-8 space-y-4">
                {/* Template Name & Color Badge */}
                <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-bold text-gray-900 leading-tight">{template.name}</h3>
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${template.color} shadow-lg ring-2 ring-white`} />
                </div>

                {/* Description */}
                <p className="text-base text-gray-700 line-clamp-2 leading-relaxed">
                    {template.description}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-3 pt-2">
                    {template.features.slice(0, 3).map((feature, index) => (
                        <span
                            key={index}
                            className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gradient-to-r from-amber-50 to-orange-50 text-amber-800 border border-amber-200 shadow-sm"
                        >
                            {feature}
                        </span>
                    ))}
                </div>

                {/* Section Count & Status */}
                <div className="flex items-center justify-between pt-4 border-t border-amber-100">
                    <span className="text-sm text-gray-600 font-medium bg-gray-50 px-3 py-1 rounded-full">
                        {template.sections.length} Sections Available
                    </span>
                    {isSelected && (
                        <span className="text-sm font-semibold text-amber-700 bg-amber-50 px-3 py-1 rounded-full border border-amber-200">
                            ✓ Currently Selected
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TemplateCard;
